"use strict";
// Copyright 2019 ProximaX Limited. All rights reserved.
// Use of this source code is governed by the Apache 2.0
// license that can be found in the LICENSE file
Object.defineProperty(exports, "__esModule", { value: true });
const UInt64_1 = require("../UInt64");
const Transaction_1 = require("./Transaction");
const TransactionVersion_1 = require("./TransactionVersion");
const AccountMetadataTransaction_1 = require("../../infrastructure/builders/AccountMetadataTransaction");
const TransactionType_1 = require("./TransactionType");
const FeeCalculationStrategy_1 = require("./FeeCalculationStrategy");
const KeyGenerator_1 = require("../../core/format/KeyGenerator");
const Convert_1 = require("../../core/format/Convert");
/**
 * Create/ modify a metadata transaction entry contains information about metadata .
 */
class AccountMetadataTransaction extends Transaction_1.Transaction {
    /**
     * @param transactionType
     * @param networkType
     * @param version
     * @param deadline
     * @param maxFee
     * @param metadataType
     * @param metadataId
     * @param modifications
     * @param signature
     * @param signer
     * @param transactionInfo
     */
    constructor(networkType, version, deadline, maxFee, scopedMetadataKey, targetPublicKey, valueSizeDelta, value, oldValue, valueSize, valueDifferences, signature, signer, transactionInfo) {
        let transactionType = TransactionType_1.TransactionType.ACCOUNT_METADATA_V2;
        super(transactionType, networkType, version, deadline, maxFee, signature, signer, transactionInfo);
        this.scopedMetadataKey = scopedMetadataKey;
        this.targetPublicKey = targetPublicKey;
        this.valueSizeDelta = valueSizeDelta;
        this.value = value;
        this.oldValue = oldValue;
        this.valueSize = valueSize;
        this.valueDifferences = valueDifferences;
    }
    static create(deadline, targetPublicKey, scopedMetadataKeyString, value, oldValue, networkType, maxFee) {
        let scopedMetadataKey = scopedMetadataKeyString instanceof UInt64_1.UInt64 ? scopedMetadataKeyString : KeyGenerator_1.KeyGenerator.generateUInt64Key(scopedMetadataKeyString);
        let valueSizeDelta = (Convert_1.Convert.utf8ToHex(value).length / 2) - (Convert_1.Convert.utf8ToHex(oldValue).length / 2);
        let valueSize = Math.max(Convert_1.Convert.utf8ToHex(value).length / 2, Convert_1.Convert.utf8ToHex(oldValue).length / 2, 0);
        let valueUint8Array = new Uint8Array(valueSize);
        valueUint8Array.set(Convert_1.Convert.hexToUint8(Convert_1.Convert.utf8ToHex(value)), 0);
        let oldValueUint8Array = new Uint8Array(valueSize);
        oldValueUint8Array.set(Convert_1.Convert.hexToUint8(Convert_1.Convert.utf8ToHex(oldValue)), 0);
        let valueDifferenceBytes = new Uint8Array(valueSize);
        for (let i = 0; i < valueSize; ++i) {
            valueDifferenceBytes[i] = valueUint8Array[i] ^ oldValueUint8Array[i];
        }
        return new AccountMetadataTransactionBuilder()
            .networkType(networkType)
            .deadline(deadline)
            .maxFee(maxFee)
            .scopedMetadataKey(scopedMetadataKey)
            .targetPublicKey(targetPublicKey)
            .valueSizeDelta(valueSizeDelta)
            .value(value)
            .valueSize(valueSize)
            .valueDifferences(valueDifferenceBytes)
            .oldValue(oldValue)
            .build();
    }
    /**
     * @override Transaction.size()
     * @description get the byte size of a transaction
     * @returns {number}
     * @memberof Transaction
     */
    get size() {
        return AccountMetadataTransaction.calculateSize(this.valueSize);
    }
    static calculateSize(valueSize) {
        const byteSize = Transaction_1.Transaction.getHeaderSize()
            + 8 // scopedMetadataKey
            + 32 // targetPublicKey - pk
            + 2 // valueDeltaSize 
            + 2 // value size
            + valueSize;
        return byteSize;
    }
    /**
     * @override Transaction.toJSON()
     * @description Serialize a transaction object - add own fields to the result of Transaction.toJSON()
     * @return {Object}
     * @memberof AccountMetadataTransaction
     */
    toJSON() {
        const parent = super.toJSON();
        return Object.assign(Object.assign({}, parent), { transaction: Object.assign(Object.assign({}, parent.transaction), { scopedMetadataKey: this.scopedMetadataKey.toDTO(), targetPublicKey: this.targetPublicKey.toDTO(), valueSizeDelta: this.valueSizeDelta, value: this.value, oldValue: this.oldValue, valueSize: this.valueSize, valueDifferences: Convert_1.Convert.uint8ToHex(this.valueDifferences) }) });
    }
    /**
     * @internal
     * @returns {VerifiableTransaction}
     */
    buildTransaction() {
        return new AccountMetadataTransaction_1.Builder()
            .addSize(this.size)
            .addType(this.type)
            .addVersion(this.versionToDTO())
            .addDeadline(this.deadline.toDTO())
            .addMaxFee(this.maxFee.toDTO())
            .addTargetPublicKey(this.targetPublicKey.publicKey)
            .addScopedMetadataKey(this.scopedMetadataKey.toDTO())
            .addValueSizeDelta(this.valueSizeDelta)
            .addValue(this.value)
            .addValueSize(this.valueSize)
            .addValueDifferences(this.valueDifferences)
            .addOldValue(this.oldValue)
            .build();
    }
}
exports.AccountMetadataTransaction = AccountMetadataTransaction;
class AccountMetadataTransactionBuilder extends Transaction_1.TransactionBuilder {
    constructor() {
        super();
        this._transactionType = TransactionType_1.TransactionType.ACCOUNT_METADATA_V2;
    }
    targetPublicKey(targetPublicKey) {
        this._targetPublicKey = targetPublicKey;
        return this;
    }
    scopedMetadataKey(scopedMetadataKey) {
        this._scopedMetadataKey = scopedMetadataKey;
        return this;
    }
    valueSizeDelta(valueSizeDelta) {
        this._valueSizeDelta = valueSizeDelta;
        return this;
    }
    value(value) {
        this._value = value;
        return this;
    }
    oldValue(oldValue) {
        this._oldValue = oldValue;
        return this;
    }
    valueSize(valueSize) {
        this._valueSize = valueSize;
        return this;
    }
    valueDifferences(_valueDifferences) {
        this._valueDifferences = _valueDifferences;
        return this;
    }
    calculateDifferences() {
        if (this._value !== undefined && this._value !== null && this._oldValue !== undefined && this._oldValue !== null) {
            this._valueSizeDelta = (Convert_1.Convert.utf8ToHex(this._value).length / 2) - (Convert_1.Convert.utf8ToHex(this._oldValue).length / 2);
            this._valueSize = Math.max(Convert_1.Convert.utf8ToHex(this._value).length / 2, Convert_1.Convert.utf8ToHex(this._oldValue).length / 2, 0);
            let valueUint8Array = new Uint8Array(this._valueSize);
            valueUint8Array.set(Convert_1.Convert.hexToUint8(Convert_1.Convert.utf8ToHex(this._value)), 0);
            let oldValueUint8Array = new Uint8Array(this._valueSize);
            oldValueUint8Array.set(Convert_1.Convert.hexToUint8(Convert_1.Convert.utf8ToHex(this._oldValue)), 0);
            let valueDifferenceBytes = new Uint8Array(this._valueSize);
            for (let i = 0; i < this._valueSize; ++i) {
                valueDifferenceBytes[i] = valueUint8Array[i] ^ oldValueUint8Array[i];
            }
            this._valueDifferences = valueDifferenceBytes;
        }
        return this;
    }
    build() {
        return new AccountMetadataTransaction(this._networkType, this._version || TransactionVersion_1.TransactionVersion.ACCOUNT_METADATA_V2, this._deadline ? this._deadline : this._createNewDeadlineFn(), this._maxFee ? this._maxFee : FeeCalculationStrategy_1.calculateFee(AccountMetadataTransaction.calculateSize(this._valueSize), this._feeCalculationStrategy), this._scopedMetadataKey, this._targetPublicKey, this._valueSizeDelta, this._value, this._oldValue, this._valueSize, this._valueDifferences, this._signature, this._signer, this._transactionInfo);
    }
}
exports.AccountMetadataTransactionBuilder = AccountMetadataTransactionBuilder;
//# sourceMappingURL=AccountMetadataTransaction.js.map